"use client";

import { useId } from "react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { useRadioGroupContext } from "./RadioGroupContext";
import type { RadioProps } from "./types";

export function Radio({
  value,
  children,
  disabled: ownDisabled,
  className,
}: RadioProps) {
  const context = useRadioGroupContext();
  const id = useId();
  const inputId = `${id}-${value}`;
  const checked = context.value === value;
  const disabled = context.disabled || ownDisabled;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "inline-flex cursor-pointer items-center gap-2",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <input
        id={inputId}
        type="radio"
        name={context.name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => context.onChange(value)}
        className="peer sr-only"
      />
      <span
        className={cn(
          "relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
          checked ? "border-blue-600" : "border-gray-300",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2",
        )}
      >
        <span
          className={cn(
            "h-2 w-2 rounded-full bg-blue-600 transition-opacity",
            checked ? "opacity-100" : "opacity-0",
          )}
        />
      </span>
      <Typography
        as="span"
        variant="body"
        color={disabled ? "muted" : "default"}
      >
        {children}
      </Typography>
    </label>
  );
}
